import { useEffect, useMemo, useState } from "react";
import Label from "../../commonComponents/label";
import Search from "../../commonComponents/search";
import { Popover, Tooltip, Modal } from "antd";
import {
  addTagsApi,
  crmTagDelete,
  crmTagsEdit,
  tagsListApi,
} from "../../../api/Api";
import {
  useAspDispatch,
  useAspSelector,
} from "../../../../../test/jest-redux-hooks";
import { updateToggleToast } from "../../../../../reduxToolkit/appSlice";
import { useSelector } from "react-redux";
import { setTagList } from "../../../../../reduxToolkit/crmSlice";
import { hexToRgba } from "../../../helpers";
import { useParams } from "react-router-dom";

let count = 0;

const Tags = ({
  toggle,
  className,
  zIndex = 60,
  Button,
  mainWidth = "w-[24vw]",
  tags,
  setTags = () => {},
  addAction,
  removeFunction,
  addFunction,
  Tag_click,
  closeAction = () => {},
  placeholder = "Search or create new tag",
  onConfirm,
  selectedItems = [],
  selectAll = false,
  totalSelectedCount = 0,
  onTagEdit = () => {},
  onTagDelete = () => {},
  ...rest
}) => {
  const [visible, setVisible] = useState(false);
  const { tagList = [] } = useSelector((store) => store.crm);
  // Local tag list for instant UI feedback
  const [localTagList, setLocalTagList] = useState(tagList);

  useEffect(() => {
    setLocalTagList(tagList);
  }, [tagList]);

  const [isFilter, setIsFilter] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    singleContactUploadDetails: { contact, address },
    singleContactDetails,
  } = useSelector((store) => store.crm);

  const selectedTags = useMemo(() => {
    return singleContactDetails["selected_tags"];
  }, [singleContactDetails?.selected_tags]);

  const handleVisibleChange = (newVisible) => {
    setVisible(newVisible);
  };
  const dispatch = useAspDispatch();
  const { currentBrand, toggleToast } = useAspSelector((state) => state.app);

  // Check if any contacts are selected - considering both individual selections and selectAll
  const hasSelectedContacts = selectAll || totalSelectedCount > 0;

  function getRandomHexColor() {
    const randomInt = () => Math.floor(Math.random() * 256);
    const componentToHex = (c) => c.toString(16).padStart(2, "0");

    const r = randomInt();
    const g = randomInt();
    const b = randomInt();

    return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
  }

  const handleAddTag = async (e, newTag) => {
    e.preventDefault();
    e.stopPropagation();

    const payLoad = {
      tag_name: newTag,
      display_color: getRandomHexColor(),
    };

    addTagsApi(currentBrand?.brand_id, payLoad).then((createTagsResponse) => {
      if (createTagsResponse?.status === 200) {
        dispatch(
          updateToggleToast([
            ...toggleToast,
            {
              id: toggleToast?.length + 1,
              content: "Tag created successfully",
              status: "Success",
              duration: 3000,
            },
          ])
        );

        const createdTag = createTagsResponse?.data;
        dispatch(setTagList((prevTags) => [...prevTags, createdTag]));
        setIsFilter("");
        tagsLists(currentBrand?.brand_id);
      } else {
        dispatch(
          updateToggleToast([
            ...toggleToast,
            {
              id: toggleToast?.length + 1,
              content:
                createTagsResponse?.response?.data?.error ||
                "Tag creation failed",
              status: "Error",
              duration: 3000,
            },
          ])
        );
      }
    });
  };

  // Get tags list
  const tagsLists = (brandId, payLoad) => {
    if (brandId) {
      tagsListApi(brandId, payLoad)
        .then((res) => {
          if (res && Array.isArray(res?.data?.tags_data)) {
            dispatch(setTagList(res?.data?.tags_data));
          } else {
            dispatch(setTagList([])); // Set to empty array if the data is invalid
          }
        })
        .finally(() => {});
    }
  };

  // Handle tag delete from Label
  const handleDeleteTag = (id) => {
    const brandId = currentBrand?.brand_id;
    crmTagDelete(id, brandId).then((res) => {
      if (res?.status === 200) {
        // Optimistically remove from local tag list
        setLocalTagList(prev => prev.filter(tag => tag.id !== id));
        if (typeof onTagDelete === 'function') {
          onTagDelete(id);
        }
      }
    });
  };

  // Handle tag edit from Label
  const handleTagEditLocal = (updatedTag) => {
    setLocalTagList(prev => prev.map(tag => tag.id === updatedTag.id ? { ...tag, ...updatedTag } : tag));
    if (typeof onTagEdit === 'function') {
      onTagEdit(updatedTag);
    }
  };

  useEffect(() => {
    if (visible) {
      tagsLists(currentBrand?.brand_id, { search: isFilter });
    }
  }, [visible, currentBrand, isFilter, selectedTags]);

  const templateSearch = (searchTerm) => {
    setIsFilter(searchTerm);
  };

  // Tag selection handler
  const handleTagToggle = (tagId) => {
    setSelectedTagIds(prev => {
      // If tag is already selected, remove it
      if (prev.includes(tagId)) {
        return prev.filter(id => id !== tagId);
      }
      // If tag is not selected, add it
      return [...prev, tagId];
    });
    // If single contact mode, assign tag immediately
    if (addFunction && typeof addFunction === 'function') {
      addFunction(tagId);
    }
  };

  const handleCloseConfirm = () => {
    setShowConfirm(false);
    setVisible(false);
    setSelectedTagIds([]);
    // Force a re-render after state updates
    setTimeout(() => {
      setShowConfirm(false);
    }, 0);
  };

  return (
    <Popover
      zIndex={zIndex}
      content={
        <div
          className={`crm-dropdown rounded-2xl bg-white p-[1vw] left-0 z-0 ${mainWidth} flex flex-col gap-2`}
        >
          {/* Show the contact's tags list */}
          {Array.isArray(tags) && tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {tags?.map((tag) => (
                <Label
                  id={tag?.id}
                  key={tag?.id}
                  label={tag?.tag}
                  color={tag?.display_color}
                  style={{ backgroundColor: hexToRgba(tag?.display_color, 0.2) }}
                  showClose={true}
                  closeAction={() => removeFunction ? removeFunction(tag?.id) : closeAction(tag?.id)}
                  disableHover={true}
                />
              ))}
            </div>
          )}
          {/* Always show selected tags at the top (bulk/multi assign mode) */}
          {((onConfirm && selectedTagIds.length > 0) || (!onConfirm && selectedTags?.length > 0)) && (
            <div className="flex flex-wrap gap-2 mb-2">
              {(onConfirm ? selectedTagIds : selectedTags).map((tagId) => {
                const tag = tagList?.find((t) => t?.id === tagId);
                if (tag) {
                  return (
                    <Label
                      id={tag?.id}
                      key={tag?.id}
                      label={tag?.tag}
                      color={tag?.display_color}
                      style={{
                        backgroundColor: hexToRgba(tag?.display_color, 0.2),
                      }}
                      showClose={true}
                      closeAction={onConfirm ? () => handleTagToggle(tag?.id) : closeAction}
                      setVisible={setVisible}
                      handleDeleteTag={handleDeleteTag}
                      disableHover={true}
                    />
                  );
                }
              })}
            </div>
          )}
          {/* Search model */}
          <div className="">
            <form>
              <Search
                placeholder={placeholder}
                onSubmit={(e, value) => handleAddTag(e, value)}
                templateSearch={templateSearch}
              />
            </form>
          </div>
          {/* Tags list */}
          <div
            className="flex flex-wrap gap-[1.5vh]"
            style={{ height: "auto", maxHeight: "15vh", overflow: "scroll" }}
          >
            {Array.isArray(localTagList) &&
              localTagList
                .filter(
                  (item) => !tags?.some((assignedTag) => assignedTag?.id === item?.id)
                )
                .filter((item) =>
                  onConfirm
                    ? !selectedTagIds.includes(item?.id)
                    : !selectedTags.includes(item?.id)
                )
                .map((item) => (
                  <Label
                    id={item?.id}
                    onClick={
                      addFunction
                        ? () => addFunction(item?.id)
                        : onConfirm
                          ? () => handleTagToggle(item?.id)
                          : addAction
                    }
                    showClose={!onConfirm}
                    key={item?.id}
                    label={item?.tag}
                    color={item?.display_color}
                    style={{
                      backgroundColor: hexToRgba(item?.display_color, 0.2),
                    }}
                    setVisible={setVisible}
                    handleDeleteTag={handleDeleteTag}
                    disableHover={false}
                    onTagEdit={handleTagEditLocal}
                  />
                ))}
          </div>
          {/* Confirm Button: only show if onConfirm is present */}
          {onConfirm && (
            <div className="flex justify-end mt-2 gap-2">
              <Tooltip
                title={!hasSelectedContacts ? "Please select contacts to assign tags" : selectedTagIds.length === 0 ? "Please select tags to assign" : ""}
                placement="top"
              >
                <button
                  className="rounded bg-[var(--new-primary)] text-white font-medium disabled:opacity-50 px-2 py-1"
                  onClick={() => { setShowConfirm({ action: 'assign' }); }}
                  disabled={selectedTagIds.length === 0 || !hasSelectedContacts}
                  type="button"
                >
                  Assign Tags
                </button>
              </Tooltip>
              <Tooltip
                title={!hasSelectedContacts ? "Please select contacts to unassign tags" : selectedTagIds.length === 0 ? "Please select tags to unassign" : ""}
                placement="top"
              >
                <button
                  className="rounded bg-[var(--new-primary)] text-white font-medium disabled:opacity-50 px-2 py-1"
                  onClick={() => { setShowConfirm({ action: 'unassign' }); }}
                  disabled={selectedTagIds.length === 0 || !hasSelectedContacts}
                  type="button"
                >
                  Unassign Tags
                </button>
              </Tooltip>
            </div>
          )}
          {showConfirm && (
            <Modal
              open={!!showConfirm}
              onCancel={handleCloseConfirm}
              footer={null}
              className={"asp-modal-popup-small"}
              closable={false}
              centered
              mask={true}
              wrapClassName={"bg-[#00000065]"}
              destroyOnClose
              getContainer={false}
              style={{
                width: "100%",
                borderRadius: 20,
                boxShadow: "0px 10px 24px 0px rgba(79, 92, 128, 0.15)",
                overflow: "hidden",
              }}
            >
              <div className="flex flex-col items-center justify-center px-4 py-6">
                <div className="mb-6 text-center text-lg font-semibold">
                  {showConfirm?.action === 'assign'
                    ? 'Are you sure you want to assign these tags?'
                    : 'Are you sure you want to unassign these tags?'}
                </div>
                <div className="flex gap-3 justify-center w-full">
                  <button
                    className="mr-10 noButton"
                    onClick={handleCloseConfirm}
                    type="button"
                  >
                    Cancel
                  </button>
                  <button
                    className={`px-6 py-2 rounded bg-[var(--new-primary)] text-white font-semibold`}
                    onClick={() => {
                      onConfirm(selectedTagIds, showConfirm?.action);
                      handleCloseConfirm();
                    }}
                    type="button"
                  >
                    Proceed
                  </button>
                </div>
              </div>
            </Modal>
          )}
        </div>
      }
      arrow={false}
      {...rest} // will contain open
      placement="bottomLeft"
      open={visible}
      onOpenChange={handleVisibleChange}
      trigger="click"
    >
      {Button}
    </Popover>
  );
};

export default Tags;
